import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import { InterventionExternalCode } from 'ts-types/api.enums';
import {
  InterventionAvailabilitySlotSearchDto,
  InterventionBookingSearchRequest,
  InterventionBookingSearchResult,
  InterventionTestBookingSearchRequest,
  InterventionTestBookingSearchResult,
  InterventionVaccinationBookingSearchRequest,
  InterventionVaccinationBookingSearchResult,
  OtpDto,
  PatientDto,
  PatientSearchRequest,
  ResourceDisplayDto,
  ResourceDto,
  TenantConfigDto
} from 'ts-types/api.types';

const {REACT_APP_API_BASE_URL: baseUrl} = process.env;
const apiUri = `${baseUrl}/api/scheduling`;

export const getInterventionBookings = (
    searchRequest: Partial<InterventionBookingSearchRequest>,
    source: CancelTokenSource): Promise<Array<InterventionBookingSearchResult>> => {

  return axios
      .post(`${apiUri}/interventions/search`, searchRequest, {cancelToken: source.token})
      .then((response) => response.data);
};

export const exportInterventionBookings = (
    request: Partial<InterventionBookingSearchRequest>,
    source: CancelTokenSource): Promise<any> => {

  return axios
      .post(`${apiUri}/interventions/search-export`, request, {cancelToken: source.token})
      .then(response => {
        const type = response.headers['content-type'];
        // @ts-ignore
        const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([BOM, response.data], {type: type});
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = response.headers['content-disposition'];
        const filenameRegex = /filename=(.*)/;
        const regexResponse = filenameRegex.exec(link.download);
        const defaultFileName = "response.csv";
        const fileName = regexResponse ?
            regexResponse[1] ? regexResponse[1] : defaultFileName :
            defaultFileName;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();

        return true;
      });
};

export const exportTestInterventionBookings = (
    request: Partial<InterventionTestBookingSearchRequest>,
    source: CancelTokenSource) => {

  return axios
      .post(`${apiUri}/interventions/search-test-export`, request, {cancelToken: source.token})
      .then(response => {
        const type = response.headers['content-type'];
        // @ts-ignore
        const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([BOM, response.data], {type: type});
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = response.headers['content-disposition'];
        const filenameRegex = /filename=(.*)/;
        const regexResponse = filenameRegex.exec(link.download);
        const defaultFileName = "response.csv";
        const fileName = regexResponse ?
            regexResponse[1] ? regexResponse[1] : defaultFileName :
            defaultFileName;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click()
      })
};

export const exportVaccinationInterventionBookings = (
    request: Partial<InterventionVaccinationBookingSearchRequest>,
    source: CancelTokenSource) => {

  return axios
      .post(`${apiUri}/interventions/search-vaccination-export`, request, {cancelToken: source.token})
      .then(response => {
        const type = response.headers['content-type'];
        // @ts-ignore
        const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([BOM, response.data], {type: type});
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = response.headers['content-disposition'];
        const filenameRegex = /filename=(.*)/;
        const regexResponse = filenameRegex.exec(link.download);
        const defaultFileName = "response.csv";
        const fileName = regexResponse ?
            regexResponse[1] ? regexResponse[1] : defaultFileName :
            defaultFileName;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click()
      })
}

export const getTestInterventionBookings = (
    searchRequest: Partial<InterventionTestBookingSearchRequest>,
    source: CancelTokenSource): Promise<Array<InterventionTestBookingSearchResult>> => {

  return axios
      .post(`${apiUri}/interventions/search-tests`, searchRequest, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getVaccinationInterventionBookings = (
    searchRequest: Partial<InterventionVaccinationBookingSearchRequest>,
    source: CancelTokenSource): Promise<Array<InterventionVaccinationBookingSearchResult>> => {

  return axios
      .post(`${apiUri}/interventions/search-vaccinations`, searchRequest, {cancelToken: source.token})
      .then((response) => response.data);
};

export const searchPatients = (
    source: CancelTokenSource,
    searchData: Partial<PatientSearchRequest>): Promise<Array<PatientDto>> => {

  return axios
      .post(`${apiUri}/search`, searchData, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getResources = (
    mandateId: number, source: CancelTokenSource): Promise<Array<ResourceDto>> => {

  const params: { [key: string]: string } = {mandateId: `${mandateId}`};

  return axios
      .get(`${apiUri}/resources`, {params, cancelToken: source.token})
      .then((response) => response.data);
};

export const getResourcesWithAvailableSlots = (
    container: string | undefined,
    interventionExternalCode: InterventionExternalCode | undefined,
    source: CancelTokenSource)
    : Promise<Array<ResourceDisplayDto>> => {

  const params: { [key: string]: string } = {};
  if (container) {
    params.container = container;
  }
  if (interventionExternalCode) {
    params.interventionType = interventionExternalCode;
  }

  return axios
      .get(`${apiUri}/resources-display`, {params, cancelToken: source.token})
      .then((response) => response.data);
};

export const searchForPatientAppointment = (
    mandateId: number,
    interventionId: number,
    source: CancelTokenSource): Promise<InterventionAvailabilitySlotSearchDto[]> => {

  const formData = new FormData();
  formData.set("mandateId", `${mandateId}`);
  formData.set("interventionId", `${interventionId}`);

  return axios
      .post(`${apiUri}/search-patient-appointment`, formData, {cancelToken: source.token})
      .then((response) => response.data);
};

export const addTenantConfig = (
    tenantConfigDto: Partial<TenantConfigDto>,
    source: CancelTokenSource)
    : Promise<TenantConfigDto> => {

  return axios
      .post(`${baseUrl}/api/tenant-config`, tenantConfigDto, {cancelToken: source.token})
      .then(response => response.data);
};

export const generateMSNTCode = (
    prefix: string, source: CancelTokenSource) => {

  const formData = new FormData();
  formData.set("prefix", prefix);

  return axios
      .post(`${apiUri}/interventions/msnt-code`, formData, {cancelToken: source.token})
      .then(response => response.data);
};

export const getOtpData = (source: CancelTokenSource): Promise<OtpDto> => {

  return axios
      .get(`${baseUrl}/api/tenant-config`, {cancelToken: source.token})
      .then(response => response.data);
};

export const isOneTimePasswordExpired = (source: CancelTokenSource): Promise<string> => {

  return axios
      .get(`${baseUrl}/api/tenant-config/one-time-password-expiration`, {cancelToken: source.token})
      .then(response => response.data);
};
